// src/admin/pages/UserListPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Search } from 'lucide-react';
import AdminTable from '../components/AdminTable.jsx';
import AdminPagination from '../components/AdminPagination.jsx';
import CreateUserModal from './CreateUserModal.jsx';
import { fetchUsers, activateUser, deactivateUser } from '../adminApi.js';

const LIMIT = 20;

export default function UserListPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchUsers({ limit: LIMIT, offset, email: emailFilter || undefined });
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [offset, emailFilter]);

  // Reset pagination offset when the email filter changes to avoid showing a
  // stale offset (e.g. offset=40 after a new search that yields 3 results).
  useEffect(() => { setOffset(0); }, [emailFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  async function handleToggleActive(user) {
    try {
      if (user.is_active) {
        await deactivateUser(user.user_id);
      } else {
        await activateUser(user.user_id);
      }
      loadUsers();
    } catch (err) {
      alert(err.message);
    }
  }

  function handleSearch(e) {
    e.preventDefault();
    setOffset(0);
  }

  const columns = [
    { key: 'email', label: 'Email' },
    {
      key: 'is_active', label: 'Stato', width: '100px',
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: row.is_active ? '#10B981' : '#EF4444',
            display: 'inline-block',
          }} />
          {row.is_active ? 'Attivo' : 'Inattivo'}
        </span>
      ),
    },
    {
      key: 'is_superadmin', label: 'Superadmin', width: '100px',
      render: (row) => row.is_superadmin ? (
        <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'rgba(232,0,45,0.08)', color: '#E8002D', fontSize: '11px', fontWeight: 600 }}>
          SA
        </span>
      ) : null,
    },
    {
      key: 'created_at', label: 'Creato il', width: '140px',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString('it-IT') : '—',
    },
    {
      key: 'actions', label: '', width: '120px',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleToggleActive(row); }}
          style={{
            padding: '4px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
            border: `1px solid ${row.is_active ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`,
            background: row.is_active ? 'rgba(239,68,68,0.06)' : 'rgba(16,185,129,0.06)',
            color: row.is_active ? '#EF4444' : '#10B981',
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {row.is_active ? 'Disattiva' : 'Attiva'}
        </button>
      ),
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: "'DM Sans', sans-serif", color: '#1d1d1f', maxWidth: '1100px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Gestione utenti</h1>
          <p style={{ color: '#6c6c6d', fontSize: '13px', margin: '4px 0 0' }}>Gestisci gli utenti della piattaforma</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: '#E8002D', color: '#fff', fontSize: '13px',
            fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <UserPlus size={15} /> Crea utente
        </button>
      </div>

      <form onSubmit={handleSearch} style={{ marginBottom: '16px' }}>
        <div style={{ position: 'relative', maxWidth: '320px' }}>
          <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c6c6d' }} />
          <input
            type="text"
            placeholder="Cerca per email..."
            value={emailFilter}
            onChange={e => setEmailFilter(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 36px', borderRadius: '8px',
              border: '1px solid rgba(0,0,0,0.1)', background: '#ffffff',
              fontSize: '13px', boxSizing: 'border-box', outline: 'none',
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        </div>
      </form>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#EF4444', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <AdminTable
          columns={columns}
          rows={users}
          loading={loading}
          emptyMessage="Nessun utente trovato"
          onRowClick={(row) => navigate(`/admin/users/${row.user_id}`)}
        />
      </div>

      <AdminPagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />

      <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={loadUsers} />
    </div>
  );
}
